import { Download, MimeType } from "@project/emerge";
import type { AbstractPartDefinition } from "@project/emerge-types";
import {
  countTotalRivets,
  getPriceForPart,
  isDuplicateSupplyPart,
  type PartLike,
  type PriceData,
  PriceUnit,
} from "@project/pricing";

const RIVET_ARTICLE_NUMBER = "12345";

const KNOWN_PART_TYPES = new Set([
  "AngleSection",
  "Pad",
  "Panel",
  "TrayPanel",
  "SlideRail",
  "Profile",
]);

interface PartsListEntry {
  articleNumber: string;
  description: string;
  amount: number;
  unit: string;
}

function priceUnitToDisplayUnit(unit: PriceUnit): string {
  switch (unit) {
    case PriceUnit.Meter:
      return "m";
    case PriceUnit.SquareMeter:
      return "m²";
    case PriceUnit.CubicMeter:
      return "m³";
    case PriceUnit.Piece:
      return "St.";
  }
}

/**
 * Generates a CSV download for the parts list (Stückliste).
 * Uses getPriceForPart / countTotalRivets from @project/pricing as single
 * source of truth for quantity calculation (shared with price calculation).
 */
export class PartsListDownload extends Download {
  private entries: PartsListEntry[] = [];

  constructor(
    partDefinitions: Map<string, AbstractPartDefinition<string>>,
    priceData?: PriceData,
  ) {
    super("Stückliste", MimeType.CSV);
    this.collectEntries(partDefinitions, priceData);
  }

  private collectEntries(
    partDefinitions: Map<string, AbstractPartDefinition<string>>,
    priceData?: PriceData,
  ): void {
    const grouped = new Map<string, PartsListEntry>();
    const allParts: PartLike[] = [];

    for (const [, part] of partDefinitions) {
      const props = part as unknown as PartLike;
      if (isDuplicateSupplyPart(props)) continue;
      allParts.push(props);

      if (!KNOWN_PART_TYPES.has(props.type) && !props.isSupplyPart) continue;

      const info = getPriceForPart(props);
      if (!info) continue;

      const key = info.code;
      const existing = grouped.get(key);

      if (existing) {
        existing.amount += info.amount;
      } else {
        grouped.set(key, {
          articleNumber: info.code,
          description: info.description,
          amount: info.amount,
          unit: priceUnitToDisplayUnit(info.unit),
        });
      }
    }

    const totalRivets = countTotalRivets(allParts, priceData);
    if (totalRivets > 0) {
      grouped.set(RIVET_ARTICLE_NUMBER, {
        articleNumber: RIVET_ARTICLE_NUMBER,
        description: "Nieten",
        amount: totalRivets,
        unit: "St.",
      });
    }

    this.entries = Array.from(grouped.values());
  }

  public override getContent(): Promise<string> {
    const header = "Part#;Bezeichnung;Menge";
    const rows = this.entries.map(
      (entry) =>
        `${entry.articleNumber};${entry.description};${entry.amount.toFixed(2)} ${entry.unit}`,
    );

    const csv = [header, ...rows].join("\n");
    return Promise.resolve(csv);
  }
}
